/**
 * Nested accordion component uses dynamic data for creating nested children as tabbed interface
 *
 * @module views/components/NestedAccordion
 * @memberof -Common
 */
import './NestedAccordion.scss';

import React, { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';

import { useFiltersColumnContext } from '@ulta/providers/FiltersColumnProvider/FiltersColumnProvider';

import NestedAccordionListItem from '../NestedAccordionListItem/NestedAccordionListItem';

/**
 * Represents a NestedAccordion component
 *
 * @method
 * @param {NestedAccordionProps} props - React properties passed from composition
 * @returns NestedAccordion
 */
export const NestedAccordion = function( props ){
  const ref = useRef();
  const { setSeeMore, toggleFilterGroupListItem } = useFiltersColumnContext();
  const { categoryData, componentKey } = props;
  const [adjustedLevel, setLevel] = useState( 0 );

  useEffect( () => {
    setLevel( getStartingCategoryLevel( { categoryData } ) );
  }, [] );

  if( !Array.isArray( categoryData ) || categoryData.length === 0 || adjustedLevel === 0 ){
    return null;
  }

  let bShowArrow = false;
  let allCategoryFilter;
  const levelOneArr = [];

  categoryData.forEach( ( item ) => {
    if( item.level === 0 ){
      bShowArrow = item.showArrow;
      allCategoryFilter = item;
    }
    else if( item.level === adjustedLevel ){
      // We need to modify the level if we're on a sub-category page as there will
      // be no nesting
      const level = adjustedLevel > 1 ? 1 : item.level;
      levelOneArr.push( { ...item, level } );
    }
  } );

  const onAllCategoriesClick = ( e ) => {
    e.preventDefault();
    onFilterGroupRootClick(
      {
        setSeeMore,
        toggleFilterGroupListItem
      },
      { filter: allCategoryFilter }
    );
  };

  return (
    <div className='NestedAccordion'
      ref={ ref }
    >
      <ul className='NestedAccordion__List'>
        { bShowArrow && allCategoryFilter?.title && (
          <li className='NestedAccordion__Label NestedAccordion__AllCategory'
            key='all-cat-id'
          >
            <Link_Huge
              url={ allCategoryFilter?.applyFilterSEOUrl }
              ariaLabel={ props.previousAccessibility }
              className='NestedAccordion__Label NestedAccordion--IsOpen'
              onClick={ onAllCategoriesClick }
              unstyled={ true }
            >
              <Icon className='NestedAccordion__BackArrow'
                size='s'
                name={ 'ArrowBack' }
                aria-hidden={ true }
              />
              { allCategoryFilter?.title }
            </Link_Huge>
          </li>
        ) }
        { levelOneArr.map( ( filterItem, i ) => (
          <NestedAccordionListItem
            key={ `${i}-filter-item-${componentKey}-${filterItem.id}` }
            category={ filterItem }
            level={ adjustedLevel }
            seeMoreLabel={ props.seeMoreLabel }
            viewLessLabel={ props.viewLessLabel }
            seeMoreListLength={ props.seeMoreListLength }
            categoryData={ props.categoryData }
            isFirstItem={ i === 0 }
            isLastItem={ i === levelOneArr.length - 1 }
            componentKey={ componentKey }
          />
        ) ) }
      </ul>
    </div>
  );
};

/**
 * Determines which level to start the tree at
 * @param {object} data helper arguments
 * @param {object} data.categoryData filter categories
 * @returns {number} level to start at
 */
export const getStartingCategoryLevel = ( data ) => {
  const { categoryData } = data || {};

  if( !Array.isArray( categoryData ) || categoryData.length === 0 ){
    return 1;
  }

  let level = 3;
  for ( const category of categoryData ){
    if( category.level > 0 && category.level < level ){
      level = category.level;
    }

    // If we're already at level 1 we can exit
    if( level === 1 ){
      break;
    }
  }

  return level;
};

/**
 * @method onFilterGroupRootClick
 * @summary Filter group root item click handler
 * @param {object} methods - passing setSeeMore, resetFilterGroupParams functions
 * @returns {undefined}
 */
export const onFilterGroupRootClick = ( methods, data ) => {
  const { setSeeMore, toggleFilterGroupListItem } = methods || {};
  const { filter } = data || {};

  if( !setSeeMore || !toggleFilterGroupListItem || !filter ){
    return;
  }

  setSeeMore( {} );
  toggleFilterGroupListItem( data.filter );
};

/**
 * Property type definitions
 *
 * @typedef NestedAccordionProps
 * @type {object}
 * @property {array} categoryData - Array of nested accordion items (name, subcategories)
 * @property {string} previousAccessibility - Sets the previous Accesibility Label
 * @property {string} seeMoreLabel - Sets the See More with item count Label
 * @property {string} viewLessLabel - Sets the view less Label
 * @property {number} seeMoreListLength - List of items to be displayed before seeMore button
 */
export const propTypes = {
  categoryData: PropTypes.arrayOf(
    PropTypes.shape( {
      name: PropTypes.string,
      subcategories: PropTypes.array
    } )
  ),
  previousAccessibility: PropTypes.string,
  seeMoreLabel: PropTypes.string,
  viewLessLabel: PropTypes.string,
  seeMoreListLength: PropTypes.number
};

NestedAccordion.propTypes = propTypes;

export default NestedAccordion;
